import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  ClientOnboardingDraft,
  createEmptyClientOnboardingDraft,
  ClientInfo,
  FarmInfo,
  PrimaryUserInfo,
  CommercialScope,
  MappingInfo,
  PlannedInfrastructureItem,
  TtnReadiness,
  normalizeOnboardingText,
  unicodeCodePointLength,
  isValidCountryCode,
  MAX_CLIENT_EMAIL_LEN,
  MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN,
  MAX_DISPLAY_NAME_LEN,
  MAX_LEGAL_NAME_LEN,
  MAX_PRIMARY_FIRST_NAME_LEN,
  MAX_PRIMARY_LAST_NAME_LEN,
  MAX_PERSISTED_COUNT,
  MAX_INFRA_LABEL_LEN,
  isValidOnboardingEmail,
} from '../../../types/clientOnboarding';
import OnboardingStepper from './OnboardingStepper';
import ClientStep from './steps/ClientStep';
import FarmStep from './steps/FarmStep';
import PrimaryUserStep from './steps/PrimaryUserStep';
import CommercialScopeStep from './steps/CommercialScopeStep';
import MappingStep from './steps/MappingStep';
import InfrastructureStep from './steps/InfrastructureStep';
import TtnReadinessStep from './steps/TtnReadinessStep';
import ReviewStep from './steps/ReviewStep';
import { validateDraft } from '../../../types/clientOnboarding';

const STEP_COUNT = 8;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableDescendants(el: HTMLElement): HTMLElement[] {
  const nodes = el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter(
    n => !(n.hidden || n.getAttribute('aria-hidden') === 'true' || n.offsetParent === null),
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// ---- R1A-06: Per-step namespaced error keys ----
export function makeStepErrors(step: number, draft: ClientOnboardingDraft, t: (k: string) => string): Record<string, string> {
  const e: Record<string, string> = {};
  const minLen = (val: string | undefined, len: number) => !val || unicodeCodePointLength(val) < len;

  if (step === 0) {
    if (minLen(draft.client.display_name, 2)) e['client.display_name'] = t('validation.required');
    // R2R-P2-04: Mirror full-draft max-length checks at step level.
    else if (draft.client.display_name && unicodeCodePointLength(draft.client.display_name) > MAX_DISPLAY_NAME_LEN)
      e['client.display_name_max'] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_DISPLAY_NAME_LEN));
    if (minLen(draft.client.legal_name, 2)) e['client.legal_name'] = t('validation.required');
    else if (draft.client.legal_name && unicodeCodePointLength(draft.client.legal_name) > MAX_LEGAL_NAME_LEN)
      e['client.legal_name_max'] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_LEGAL_NAME_LEN));
    // R2J-05 / R2R-P2-01: country must be an assigned ISO 3166-1 alpha-2 code
    if (!draft.client.country || !isValidCountryCode(draft.client.country))
      e['client.country'] = t('validation.invalidCountry');
    // R2R-03: Canonical email validation at step level — uses the
    // same authoritative isValidOnboardingEmail predicate as validateDraft.
    if (!draft.client.email || !isValidOnboardingEmail(draft.client.email))
      e['client.email'] = t('validation.invalidEmail');
    // R2Q-01: Application-level client email total length (254 chars)
    else if (unicodeCodePointLength(draft.client.email) > MAX_CLIENT_EMAIL_LEN)
      e['client.email_max'] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_CLIENT_EMAIL_LEN));
  }
  if (step === 1) {
    if (draft.farms.length === 0) e['farms.empty'] = t('validation.farmRequired');
    draft.farms.forEach((f, i) => {
      if (minLen(f.farm_name, 2)) e[`farms.${i}.farm_name`] = t('validation.required');
      if (f.country && normalizeOnboardingText(f.country).length > 0 && !isValidCountryCode(f.country))
        e[`farms.${i}.country`] = t('validation.invalidCountry');
      if (f.surface_ha != null && (typeof f.surface_ha !== 'number' || !isFinite(f.surface_ha) || f.surface_ha < 0))
        e[`farms.${i}.surface_ha`] = t('validation.invalidSurfaceArea');
      // R2N-01: Coordinate pair validation — both present or both absent
      const hasLat = f.latitude != null;
      const hasLon = f.longitude != null;
      if (hasLat !== hasLon) {
        if (!hasLat) e[`farms.${i}.latitude`] = t('validation.coordinatesUnpaired');
        if (!hasLon) e[`farms.${i}.longitude`] = t('validation.coordinatesUnpaired');
      }
      if (f.latitude != null && (typeof f.latitude !== 'number' || !isFinite(f.latitude) || f.latitude < -90 || f.latitude > 90))
        e[`farms.${i}.latitude`] = t('validation.invalidLatitude');
      if (f.longitude != null && (typeof f.longitude !== 'number' || !isFinite(f.longitude) || f.longitude < -180 || f.longitude > 180))
        e[`farms.${i}.longitude`] = t('validation.invalidLongitude');
    });
  }
  if (step === 2) {
    // R2J parity: backend min_length=1, frontend must match
    if (minLen(draft.primary_user.first_name, 1)) e['primary_user.first_name'] = t('validation.required');
    // R2R-P2-02: Mirror full-draft max-length checks at step level.
    else if (draft.primary_user.first_name && unicodeCodePointLength(draft.primary_user.first_name) > MAX_PRIMARY_FIRST_NAME_LEN)
      e['primary_user.first_name_max'] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_PRIMARY_FIRST_NAME_LEN));
    if (minLen(draft.primary_user.last_name, 1)) e['primary_user.last_name'] = t('validation.required');
    else if (draft.primary_user.last_name && unicodeCodePointLength(draft.primary_user.last_name) > MAX_PRIMARY_LAST_NAME_LEN)
      e['primary_user.last_name_max'] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_PRIMARY_LAST_NAME_LEN));
    // R2R-03: Canonical email validation at step level —
    // uses the same authoritative isValidOnboardingEmail predicate.
    if (!draft.primary_user.email || !isValidOnboardingEmail(draft.primary_user.email))
      e['primary_user.email'] = t('validation.invalidEmail');
    // R2M-05: Cognito ListUsers filter-safe length (246 code points)
    else if (unicodeCodePointLength(draft.primary_user.email) > MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN)
      e['primary_user.email'] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN));
  }
  if (step === 3) {
    // R2R-P2-05: Mirror full-draft safe-integer and MAX_PERSISTED_COUNT
    // semantics at step level.  Number.isSafeInteger rejects values
    // beyond 2^53-1 that would fail backend validation.
    const checkCount = (val: any, key: string) => {
      if (!Number.isSafeInteger(val) || val < 0) {
        e[key] = t('validation.nonNegativeInteger');
      } else if (val > MAX_PERSISTED_COUNT) {
        e[key] = t('validation.countTooLarge');
      }
    };
    checkCount(draft.commercial_scope.command_packs, 'commercial.command_packs');
    checkCount(draft.commercial_scope.vision_packs, 'commercial.vision_packs');
    checkCount(draft.commercial_scope.ia_packs, 'commercial.ia_packs');
    checkCount(draft.commercial_scope.planned_gateways, 'commercial.planned_gateways');
    if (draft.commercial_scope.pump_control && draft.commercial_scope.pump_rated_power_kw != null) {
      const pw = draft.commercial_scope.pump_rated_power_kw;
      if (typeof pw !== 'number' || !isFinite(pw) || pw <= 0)
        e['commercial.pump_power'] = t('validation.positiveNumber');
    }
  }
  if (step === 4) {
    if (!Number.isInteger(draft.mapping.manual_count) || draft.mapping.manual_count < 0)
      e['mapping.manual_count'] = t('validation.nonNegativeInteger');
  }
  if (step === 5) {
    draft.infrastructure.forEach((item) => {
      if (minLen(item.id, 1)) e[`infra.${item.id}.id`] = t('validation.required');
      // R2R-P2: Mirror full-draft MAX_INFRA_LABEL_LEN at step level.
      // Required and over-length are mutually exclusive; both route through
      // infra.<id>.label so that InfrastructureStep renders the error inline
      // with aria-invalid + aria-describedby + role=alert without needing
      // a new error key.
      if (minLen(item.label, 1)) e[`infra.${item.id}.label`] = t('validation.required');
      else if (item.label && unicodeCodePointLength(item.label) > MAX_INFRA_LABEL_LEN)
        e[`infra.${item.id}.label`] = t('validation.fieldTooLong').replace('{{max}}', String(MAX_INFRA_LABEL_LEN));
      if (!Number.isInteger(item.quantity) || item.quantity < 1)
        e[`infra.${item.id}.quantity`] = t('validation.positiveInteger');
    });
  }
  return e;
}

const ClientOnboardingWizard: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['admin_client_onboarding', 'common']);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<ClientOnboardingDraft>(() => createEmptyClientOnboardingDraft());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftErrorKeys, setDraftErrorKeys] = useState<string[]>([]);

  // ---- R1A-04: Modal accessibility ----
  const dialogRef = useRef<HTMLDivElement>(null);
  const stepContentRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = 'cow-dialog-title';
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // ---- R1A-04: Body scroll lock ----
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // ---- R1E-04: Focus management on step change ----
  useEffect(() => {
    if (!isOpen) return;
    // Small delay to allow new step content to render
    const timer = setTimeout(() => {
      const content = stepContentRef.current;
      if (!content) return;
      // Focus the first focusable element in the new step, or the heading
      const focusable = getFocusableDescendants(content);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        // Focus the heading as a fallback
        const heading = content.querySelector('h3') as HTMLElement | null;
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [step, isOpen]);

  // ---- R1A-04: Focus trap + Escape ----
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (document.activeElement instanceof HTMLElement) {
      restoreRef.current = document.activeElement;
    }

    const focusable = getFocusableDescendants(dialog);
    if (focusable.length > 0) focusable[0].focus();
    else dialog.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const f = getFocusableDescendants(dialog);
      if (f.length === 0) { e.preventDefault(); dialog.focus(); return; }
      const first = f[0], last = f[f.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && (active === first || active === dialog)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || active === dialog)) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (restoreRef.current?.isConnected) restoreRef.current.focus();
      restoreRef.current = null;
    };
  }, [isOpen]);

  const stepLabels = [
    t('stepper.step1'), t('stepper.step2'), t('stepper.step3'),
    t('stepper.step4'), t('stepper.step5'), t('stepper.step6'),
    t('stepper.step7'), t('stepper.step8'),
  ];

  // ---- R1A-06: Clear stale errors on navigation ----
  const goToStep = useCallback((newStep: number) => {
    setErrors({});
    setStep(newStep);
  }, []);

  const validateStep = useCallback((s: number): boolean => {
    const e = makeStepErrors(s, draft, t);
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [draft, t]);

  const next = () => {
    if (validateStep(step)) goToStep(Math.min(STEP_COUNT - 1, step + 1));
  };
  const back = () => goToStep(Math.max(0, step - 1));

  // ---- R1A-02: Full draft validation (runs at review step) ----
  useEffect(() => {
    if (step === 7) setDraftErrorKeys(validateDraft(draft));
  }, [step, draft]);

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 outline-none"
      data-testid="client-onboarding-wizard-overlay"
    >
      <div className="bg-[var(--surface)] rounded-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl" data-testid="client-onboarding-wizard">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-light)]">
          <h2 id={titleId} className="text-heading-3 text-[var(--text-primary)]" data-testid="onboarding-title">
            {t('title')}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--gray-100)]" aria-label={t('common:close')} data-testid="onboarding-close">
            <X className="w-5 h-5 text-[var(--text-tertiary)]" />
          </button>
        </div>

        {/* Stepper */}
        <OnboardingStepper step={step} stepLabels={stepLabels} ariaLabel={t('stepper.ariaLabel')} />

        {/* Body */}
        <div ref={stepContentRef} className="px-6 py-5 overflow-y-auto flex-1">
          {step === 0 && <ClientStep data={draft.client} onChange={(c: Partial<ClientInfo>) => setDraft(d => ({ ...d, client: c }))} errors={errors} />}
          {step === 1 && <FarmStep farms={draft.farms} onChange={(f: FarmInfo[]) => setDraft(d => ({ ...d, farms: f }))} errors={errors} />}
          {step === 2 && <PrimaryUserStep data={draft.primary_user} onChange={(u: Partial<PrimaryUserInfo>) => setDraft(d => ({ ...d, primary_user: u }))} errors={errors} />}
          {step === 3 && <CommercialScopeStep data={draft.commercial_scope} onChange={(o: Partial<CommercialScope>) => setDraft(d => ({ ...d, commercial_scope: o }))} errors={errors} />}
          {step === 4 && <MappingStep data={draft.mapping} onChange={(m: MappingInfo) => setDraft(d => ({ ...d, mapping: m }))} errors={errors} />}
          {step === 5 && <InfrastructureStep items={draft.infrastructure} onChange={(i: PlannedInfrastructureItem[]) => setDraft(d => ({ ...d, infrastructure: i }))} errors={errors} />}
          {step === 6 && <TtnReadinessStep data={draft.ttn_readiness} />}
          {step === 7 && <ReviewStep draft={draft} errorKeys={draftErrorKeys} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-light)]">
          {step < STEP_COUNT - 1 ? (
            <>
              <button onClick={step === 0 ? onClose : back}
                className="flex items-center gap-1 px-4 py-2 text-[var(--text-secondary)] border border-[var(--border-light)] rounded-lg hover:bg-[var(--gray-50)]"
                data-testid="onboarding-back">
                {step === 0 ? t('common:cancel') : (<><ChevronLeft className="w-4 h-4" /> {t('common:back')}</>)}
              </button>
              <button onClick={next}
                className="flex items-center gap-1 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90"
                data-testid="onboarding-next">
                {t('common:next')} <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button onClick={back}
                className="flex items-center gap-1 px-4 py-2 text-[var(--text-secondary)] border border-[var(--border-light)] rounded-lg hover:bg-[var(--gray-50)]"
                data-testid="onboarding-back">
                <ChevronLeft className="w-4 h-4" /> {t('step8.back')}
              </button>
              <button onClick={onClose}
                className="flex items-center gap-1 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90"
                data-testid="onboarding-done">
                {t('common:close')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientOnboardingWizard;
